// client/App.tsx
import React, { Suspense, useEffect, lazy } from "react";
import "./global.css";

import { Analytics } from "@/components/Analytics";
import { DeferredToasters } from "@/components/DeferredToasters";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import Index from "./pages/Index";
import MenuPreloadScripts from "@/components/MenuPreloadScripts";
import ScrollToTop from "@/components/ScrollToTop";
const NotFound = lazy(() => import("./pages/NotFound"));
const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Solutions = lazy(() => import("@/pages/SolutionsHub"));
const Products = lazy(() => import("@/pages/Products"));
const Services = lazy(() => import("@/pages/Service"));
const Partners = lazy(() => import("@/pages/Partners"));
const Support = lazy(() => import("@/pages/Support"));
const Company = lazy(() => import("@/pages/Company"));
const GetADemo = lazy(() => import("@/pages/GetADemo"));
const TryForFree = lazy(() => import("@/pages/TryForFree"));
const ResourceLibrary = lazy(() => import("@/pages/ResourceLibrary"));
const PartnerConnect = lazy(() => import("@/pages/PartnerConnect"));
const SupportHub = lazy(() => import("@/pages/SupportHub"));
const Careers = lazy(() => import("@/pages/Careers"));
const SolutionDetail = lazy(() => import("@/pages/solutions/Detail"));
const ProductDetail = lazy(() => import("@/pages/products/Detail"));
const ServiceDetail = lazy(() => import("@/pages/services/Detail"));
const PartnerDetail = lazy(() => import("@/pages/partners/Detail"));
const CompanyDetail = lazy(() => import("@/pages/company/Detail"));
const FrenchDetail = lazy(() => import("@/pages/FrenchDetail"));

const queryClient = new QueryClient();

export default function App() {
  // Apply dark theme immediately during render for SSR consistency
  // This ensures both server and client have the same initial state
  if (typeof document !== 'undefined' && !document.documentElement.classList.contains('dark')) {
    document.documentElement.classList.add('dark');
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Analytics />
      <MenuPreloadScripts />
      <TooltipProvider>
        <DeferredToasters />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/onplexus-global-currency-orchestration-platform" element={<Index />} />
              <Route path="/fr" element={<Index lang="fr" />} />
              <Route path="/fr/accueil" element={<Index lang="fr" />} />
              <Route path="/blog" element={<BlogIndex />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/fr/blog" element={<BlogIndex lang="fr" />} />
              <Route path="/fr/blog/:slug" element={<BlogPost lang="fr" />} />

              {/* English routes */}
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/solutions/*" element={<SolutionDetail />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/*" element={<ProductDetail />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/*" element={<ServiceDetail />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/partners/*" element={<PartnerDetail />} />
              <Route path="/support" element={<Support />} />
              <Route path="/company" element={<Company />} />
              <Route path="/company/*" element={<CompanyDetail />} />

              {/* French routes */}
              <Route path="/fr/solutions" element={<Solutions lang="fr" />} />
              <Route path="/fr/solutions/*" element={<SolutionDetail lang="fr" />} />
              <Route path="/fr/fr-solutions" element={<Solutions lang="fr" />} />
              <Route path="/fr/products" element={<Products lang="fr" />} />
              <Route path="/fr/products/*" element={<ProductDetail lang="fr" />} />
              <Route path="/fr/services" element={<Services lang="fr" />} />
              <Route path="/fr/services/*" element={<ServiceDetail lang="fr" />} />
              <Route path="/fr/partners" element={<Partners lang="fr" />} />
              <Route path="/fr/partners/*" element={<PartnerDetail lang="fr" />} />
              <Route path="/fr/support" element={<Support lang="fr" />} />
              <Route path="/fr/company" element={<Company lang="fr" />} />
              <Route path="/fr/company/*" element={<CompanyDetail lang="fr" />} />

              {/* Special routes */}
              <Route path="/get-a-demo" element={<GetADemo />} />
              <Route path="/try-for-free" element={<TryForFree />} />
              <Route path="/resources" element={<ResourceLibrary />} />
              <Route path="/partner-connect" element={<PartnerConnect />} />
              <Route path="/support-hub" element={<SupportHub />} />
              <Route path="/careers" element={<Careers />} />

              {/* French catch-all for pages without specific routes */}
              <Route path="/fr/*" element={<FrenchDetail />} />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
